/**
 * @fileoverview Process Service for managing organizational processes
 * Processes can be Verbal, Documented, or Undocumented
 * Documented processes link to documents in the document library
 */

import { db } from '@/js/external-js/config/main_config';
import { Timestamp } from 'firebase/firestore';
import { createDocument } from './documentsService';
import { SupportingDocs } from '@/js/external-js/variableModules/namespaces/SupportingDocs.namespace';
import { getAuthUID } from '@/lib/authState';

/** Process types */
export type ProcessType = 'verbal' | 'documented' | 'undocumented';

/** Process status */
export type ProcessStatus = 'Draft' | 'Active' | 'Under Review' | 'Archived';

/** Repeat frequency options */
export type ProcessRepeatFrequency = 'never' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually';

/** Repeat configuration for recurring processes */
export interface ProcessRepeatConfig {
  frequency: ProcessRepeatFrequency;
  interval: number; // e.g., every 2 weeks
  startDate?: Date | Timestamp;
  endDate?: Date | Timestamp | null;
}

/** Document library reference for processes synced from document library */
export interface DocumentLibraryReference {
  /** Document library document ID */
  documentId: string;
  /** Document library's component ID (e.g., DOC-123) */
  documentComponentId?: string;
  /** Document version */
  documentVersion?: string;
  /** When the document was synced to processes */
  syncedAt: Date | Timestamp;
  /** File name for attachment display */
  fileName?: string;
  /** File URL for download/preview */
  fileUrl?: string;
}

/** Process interface */
export interface Process {
  id: string;
  /** Consumer-facing ID (PRO-1, PRO-2, etc.) */
  componentId?: string;
  name: string;
  description: string;
  type: ProcessType;
  status: ProcessStatus;
  owner: string;
  /** Document library document ID (for documented processes) - kept for backwards compatibility */
  documentId?: string;
  /** Full document library reference with metadata */
  documentLibraryRef?: DocumentLibraryReference;
  /** Related control references */
  relatedControls?: string[];
  /** Business unit */
  businessUnit?: string;
  /** Category/department */
  category?: string;
  /** Repeat/recurrence configuration */
  repeat?: ProcessRepeatConfig;
  /** Review date (for non-recurring) */
  reviewDate?: Date | Timestamp;
  /** Last review date */
  lastReviewDate?: Date | Timestamp;
  /** Next review date (calculated from repeat config or manual) */
  nextReviewDate?: Date | Timestamp;
  /** Created date */
  createdAt: Date | Timestamp;
  /** Created by user ID */
  createdBy: string;
  /** Last updated date */
  updatedAt: Date | Timestamp;
  /** Last updated by user ID */
  updatedBy: string;
  /** Additional metadata */
  metadata?: Record<string, any>;
}

/** Input for creating a new process */
export interface CreateProcessInput {
  name: string;
  description: string;
  type: ProcessType;
  status?: ProcessStatus;
  owner?: string;
  relatedControls?: string[];
  businessUnit?: string;
  category?: string;
  /** Repeat/recurrence configuration */
  repeat?: ProcessRepeatConfig;
  /** Review date (for non-recurring processes) */
  reviewDate?: Date;
  /** File to upload for documented processes */
  file?: File;
  /** Existing document ID to link (for documented processes) */
  existingDocumentId?: string;
}

/**
 * Get the processes collection reference for a company
 */
const getProcessesCollection = (companyID: string) => {
  return db.collection(`customers/${companyID}/processes`);
};

/**
 * Get the next available componentId for a process (PRO-1, PRO-2, etc.)
 */
async function getNextComponentId(companyID: string): Promise<string> {
  // Get all existing componentIds to find the highest number
  const snapshot = await getProcessesCollection(companyID)
    .orderBy('componentId', 'desc')
    .limit(1)
    .get();
  
  let nextNumber = 1;
  
  if (!snapshot.empty) {
    const lastProcess = snapshot.docs[0].data();
    const lastComponentId = lastProcess.componentId;
    if (lastComponentId && typeof lastComponentId === 'string') {
      const match = lastComponentId.match(/^PRO-(\d+)$/);
      if (match) {
        nextNumber = parseInt(match[1], 10) + 1;
      }
    }
  }
  
  // If no valid componentId found, count all processes to be safe
  if (nextNumber === 1) {
    const countSnapshot = await getProcessesCollection(companyID).get();
    nextNumber = countSnapshot.size + 1;
  }
  
  return `PRO-${nextNumber}`;
}

/**
 * Ensure all processes have a componentId (PRO-1, PRO-2, etc.)
 * This is used to retroactively assign componentIds to processes that don't have one
 * Returns the number of processes that were updated
 */
export async function ensureProcessComponentIds(companyID: string): Promise<number> {
  const snapshot = await getProcessesCollection(companyID).get();
  
  // Find processes without a valid componentId
  const processesWithoutComponentId = snapshot.docs.filter(doc => {
    const data = doc.data();
    const componentId = data.componentId;
    // Check if componentId is missing or doesn't match PRO-N format
    return !componentId || typeof componentId !== 'string' || !componentId.match(/^PRO-\d+$/);
  });
  
  if (processesWithoutComponentId.length === 0) {
    console.log('[ProcessService] All processes have valid componentIds');
    return 0;
  }
  
  console.log(`[ProcessService] Found ${processesWithoutComponentId.length} processes without valid componentIds`);
  
  // Find the highest existing componentId number
  let maxNumber = 0;
  snapshot.docs.forEach(doc => {
    const data = doc.data();
    const componentId = data.componentId;
    if (componentId && typeof componentId === 'string') {
      const match = componentId.match(/^PRO-(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNumber) {
          maxNumber = num;
        }
      }
    }
  });
  
  // Assign componentIds to processes that don't have one
  const batch = db.batch();
  let nextNumber = maxNumber + 1;
  
  for (const doc of processesWithoutComponentId) {
    const componentId = `PRO-${nextNumber}`;
    batch.update(doc.ref, { componentId });
    console.log(`[ProcessService] Assigning ${componentId} to process ${doc.id}`);
    nextNumber++;
  }
  
  await batch.commit();
  console.log(`[ProcessService] Assigned componentIds to ${processesWithoutComponentId.length} processes`);
  
  return processesWithoutComponentId.length;
}

/**
 * Create a new process
 * For documented processes, this will also create a document in the document library
 */
export async function createProcess(
  companyID: string,
  input: CreateProcessInput
): Promise<Process> {
  const userId = getAuthUID() || 'unknown';
  const now = new Date();
  
  let documentId: string | undefined;
  
  // For documented processes, create or link a document
  if (input.type === 'documented') {
    if (input.existingDocumentId) {
      // Link to existing document
      documentId = input.existingDocumentId;
    } else if (input.file) {
      // Create new document in document library
      const docTemplate = {
        fileName: input.file.name,
        displayName: input.name,
        documentType: 'Procedure',
        status: input.status || 'Draft',
        uploadedBy: userId,
        procRef: input.relatedControls || [],
        businessUnit: input.businessUnit || '',
        classification: 'Business Confidential',
      } as any;
      
      const newDoc = await createDocument(docTemplate, input.file, companyID);
      documentId = newDoc.docID?.toString();
      console.log('[ProcessService] Created document for process:', documentId);
    }
  }
  
  // Calculate next review date based on repeat config or review date
  let nextReviewDate: Date | undefined;
  let reviewDate: Date | undefined = input.reviewDate;
  
  if (input.repeat && input.repeat.frequency !== 'never') {
    // For recurring processes, use start date as next review
    nextReviewDate = input.repeat.startDate instanceof Date 
      ? input.repeat.startDate 
      : input.repeat.startDate 
        ? new Date((input.repeat.startDate as any).seconds * 1000)
        : now;
  } else if (input.reviewDate) {
    // For non-recurring, use the specified review date
    nextReviewDate = input.reviewDate;
    reviewDate = input.reviewDate;
  }
  
  // Generate the next componentId (PRO-1, PRO-2, etc.)
  const componentId = await getNextComponentId(companyID);
  
  // Create the process document
  const processData: Omit<Process, 'id'> = {
    componentId,
    name: input.name,
    description: input.description,
    type: input.type,
    status: input.status || 'Draft',
    owner: input.owner || userId,
    documentId,
    relatedControls: input.relatedControls || [],
    businessUnit: input.businessUnit || '',
    category: input.category || '',
    repeat: input.repeat,
    reviewDate,
    lastReviewDate: now,
    nextReviewDate,
    createdAt: now,
    createdBy: userId,
    updatedAt: now,
    updatedBy: userId,
  };
  
  const docRef = await getProcessesCollection(companyID).add(processData);
  
  console.log('[ProcessService] Created process:', docRef.id, 'componentId:', componentId);
  
  return {
    id: docRef.id,
    ...processData,
  };
}

/**
 * Get all processes for a company
 */
export async function getProcesses(companyID: string): Promise<Process[]> {
  const snapshot = await getProcessesCollection(companyID).get();
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      componentId: data.componentId,
      name: data.name || '',
      description: data.description || '',
      type: data.type || 'undocumented',
      status: data.status || 'Draft',
      owner: data.owner || '',
      documentId: data.documentId,
      documentLibraryRef: data.documentLibraryRef ? {
        documentId: data.documentLibraryRef.documentId,
        documentComponentId: data.documentLibraryRef.documentComponentId,
        documentVersion: data.documentLibraryRef.documentVersion,
        syncedAt: data.documentLibraryRef.syncedAt?.toDate?.() || data.documentLibraryRef.syncedAt,
        fileName: data.documentLibraryRef.fileName,
        fileUrl: data.documentLibraryRef.fileUrl,
      } : undefined,
      relatedControls: data.relatedControls || [],
      businessUnit: data.businessUnit || '',
      category: data.category || '',
      repeat: data.repeat,
      reviewDate: data.reviewDate?.toDate?.() || data.reviewDate,
      lastReviewDate: data.lastReviewDate?.toDate?.() || data.lastReviewDate,
      nextReviewDate: data.nextReviewDate?.toDate?.() || data.nextReviewDate,
      createdAt: data.createdAt?.toDate?.() || data.createdAt || new Date(),
      createdBy: data.createdBy || '',
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt || new Date(),
      updatedBy: data.updatedBy || '',
      metadata: data.metadata,
    } as Process;
  });
}

/**
 * Get a single process by ID
 */
export async function getProcess(companyID: string, processId: string): Promise<Process | null> {
  const doc = await getProcessesCollection(companyID).doc(processId).get();
  
  if (!doc.exists) {
    return null;
  }
  
  const data = doc.data();
  return {
    id: doc.id,
    componentId: data?.componentId,
    name: data?.name || '',
    description: data?.description || '',
    type: data?.type || 'undocumented',
    status: data?.status || 'Draft',
    owner: data?.owner || '',
    documentId: data?.documentId,
    documentLibraryRef: data?.documentLibraryRef ? {
      documentId: data.documentLibraryRef.documentId,
      documentComponentId: data.documentLibraryRef.documentComponentId,
      documentVersion: data.documentLibraryRef.documentVersion,
      syncedAt: data.documentLibraryRef.syncedAt?.toDate?.() || data.documentLibraryRef.syncedAt,
      fileName: data.documentLibraryRef.fileName,
      fileUrl: data.documentLibraryRef.fileUrl,
    } : undefined,
    relatedControls: data?.relatedControls || [],
    businessUnit: data?.businessUnit || '',
    category: data?.category || '',
    repeat: data?.repeat,
    reviewDate: data?.reviewDate?.toDate?.() || data?.reviewDate,
    lastReviewDate: data?.lastReviewDate?.toDate?.() || data?.lastReviewDate,
    nextReviewDate: data?.nextReviewDate?.toDate?.() || data?.nextReviewDate,
    createdAt: data?.createdAt?.toDate?.() || data?.createdAt || new Date(),
    createdBy: data?.createdBy || '',
    updatedAt: data?.updatedAt?.toDate?.() || data?.updatedAt || new Date(),
    updatedBy: data?.updatedBy || '',
    metadata: data?.metadata,
  } as Process;
}

/**
 * Update a process
 */
export async function updateProcess(
  companyID: string,
  processId: string,
  updates: Partial<Omit<Process, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  const userId = getAuthUID() || 'unknown';
  
  await getProcessesCollection(companyID).doc(processId).update({
    ...updates,
    updatedAt: new Date(),
    updatedBy: userId,
  });
  
  console.log('[ProcessService] Updated process:', processId);
}

/**
 * Delete a process
 * Note: This does NOT delete the linked document from the document library
 */
export async function deleteProcess(companyID: string, processId: string): Promise<void> {
  await getProcessesCollection(companyID).doc(processId).delete();
  console.log('[ProcessService] Deleted process:', processId);
}

/**
 * Sync document library procedures to the processes collection
 * This creates process entries for any document library procedures that don't already have one
 * Returns the number of new processes created
 */
export async function syncDocumentLibraryProcedures(
  companyID: string,
  docProcedures: Array<{
    id: string;
    name?: string;
    policy_name?: string;
    description?: string;
    status?: string;
    policy_status?: string;
    owner?: string;
    policy_history?: { policy_owner?: string };
    category?: string;
    businessUnit?: string;
    nextReviewDate?: Date;
    relatedControls?: string[];
    created?: Date;
    last_reviewed?: Date;
    // Document library specific fields for back reference
    componentID?: string; // Document library's own component ID
    policy_version?: string;
    policy_metadata?: { policyType?: string; documentCategory?: string };
    // File info for attachment display
    fileName?: string;
    fileUrl?: string;
  }>
): Promise<number> {
  const userId = getAuthUID() || 'system';
  
  // Get existing processes
  const existingProcesses = await getProcesses(companyID);
  
  // Create a set of document IDs that already have a process entry
  const existingDocIds = new Set(
    existingProcesses
      .filter(p => p.documentId)
      .map(p => p.documentId)
  );
  
  // Find document library procedures that don't have a process entry
  const newProcedures = docProcedures.filter(doc => !existingDocIds.has(doc.id));
  
  if (newProcedures.length === 0) {
    console.log('[ProcessService] No new document library procedures to sync');
    return 0;
  }
  
  console.log(`[ProcessService] Syncing ${newProcedures.length} document library procedures to processes collection`);
  
  // Get the starting componentId number
  let nextNumber = 1;
  const existingComponentIds = existingProcesses
    .map(p => p.componentId)
    .filter(Boolean)
    .map(id => {
      const match = id?.match(/^PRO-(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    });
  
  if (existingComponentIds.length > 0) {
    nextNumber = Math.max(...existingComponentIds) + 1;
  } else if (existingProcesses.length > 0) {
    nextNumber = existingProcesses.length + 1;
  }
  
  // Create process entries for each new procedure
  const batch = db.batch();
  const processesRef = getProcessesCollection(companyID);
  
  const now = new Date();
  
  for (const doc of newProcedures) {
    const processRef = processesRef.doc();
    const componentId = `PRO-${nextNumber}`;
    nextNumber++;
    
    // Build the document library reference for back-linking
    const documentLibraryRef: DocumentLibraryReference = {
      documentId: doc.id,
      documentComponentId: doc.componentID || undefined,
      documentVersion: doc.policy_version || '1.0',
      syncedAt: now,
      fileName: doc.fileName || undefined,
      fileUrl: doc.fileUrl || undefined,
    };
    
    const processData = {
      componentId,
      name: doc.name || doc.policy_name || 'Untitled Process',
      description: doc.description || '',
      type: 'documented' as ProcessType,
      status: (doc.status || doc.policy_status || 'Draft') as ProcessStatus,
      owner: doc.owner || doc.policy_history?.policy_owner || '',
      documentId: doc.id, // Keep for backwards compatibility
      documentLibraryRef, // Full reference with metadata
      relatedControls: doc.relatedControls || [],
      category: doc.category || '',
      businessUnit: doc.businessUnit || '',
      reviewDate: doc.nextReviewDate || null,
      nextReviewDate: doc.nextReviewDate || null,
      createdAt: doc.created || new Date(),
      updatedAt: doc.last_reviewed || new Date(),
      createdBy: userId,
      updatedBy: userId,
    };
    
    batch.set(processRef, processData);
  }
  
  await batch.commit();
  console.log(`[ProcessService] Successfully synced ${newProcedures.length} procedures to processes collection`);
  
  return newProcedures.length;
}

/**
 * Get a process by its linked document ID
 */
export async function getProcessByDocumentId(companyID: string, documentId: string): Promise<Process | null> {
  const snapshot = await getProcessesCollection(companyID)
    .where('documentId', '==', documentId)
    .limit(1)
    .get();
  
  if (snapshot.empty) {
    return null;
  }
  
  const doc = snapshot.docs[0];
  const data = doc.data();
  
  return {
    id: doc.id,
    componentId: data.componentId,
    name: data.name,
    description: data.description,
    type: data.type,
    status: data.status,
    owner: data.owner,
    documentId: data.documentId,
    documentLibraryRef: data.documentLibraryRef ? {
      documentId: data.documentLibraryRef.documentId,
      documentComponentId: data.documentLibraryRef.documentComponentId,
      documentVersion: data.documentLibraryRef.documentVersion,
      syncedAt: data.documentLibraryRef.syncedAt?.toDate?.() || data.documentLibraryRef.syncedAt,
      fileName: data.documentLibraryRef.fileName,
      fileUrl: data.documentLibraryRef.fileUrl,
    } : undefined,
    relatedControls: data.relatedControls || [],
    category: data.category,
    businessUnit: data.businessUnit,
    repeat: data.repeat,
    reviewDate: data.reviewDate?.toDate?.() || data.reviewDate,
    nextReviewDate: data.nextReviewDate?.toDate?.() || data.nextReviewDate,
    createdAt: data.createdAt?.toDate?.() || data.createdAt,
    updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
    createdBy: data.createdBy,
    updatedBy: data.updatedBy,
  };
}

/**
 * Get process type display label
 */
export function getProcessTypeLabel(type: ProcessType): string {
  switch (type) {
    case 'verbal':
      return 'Verbal';
    case 'documented':
      return 'Documented';
    case 'undocumented':
      return 'Undocumented';
    default:
      return 'Unknown';
  }
}

/**
 * Get process type description
 */
export function getProcessTypeDescription(type: ProcessType): string {
  switch (type) {
    case 'verbal':
      return 'A process communicated verbally without formal documentation';
    case 'documented':
      return 'A formally documented process with supporting documentation';
    case 'undocumented':
      return 'A process that exists but has not yet been documented';
    default:
      return '';
  }
}

export default {
  createProcess,
  getProcesses,
  getProcess,
  updateProcess,
  deleteProcess,
  syncDocumentLibraryProcedures,
  getProcessByDocumentId,
  ensureProcessComponentIds,
  getProcessTypeLabel,
  getProcessTypeDescription,
};
